import React from 'react';
import { ArrowRight, Search, Sparkles } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { DEMO_MERCHANTS } from '../../constants/demo-copy';
import { AnimatedNumber, useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  MerchantDot,
  ProductMockupBody,
  ProductMockupFrame
} from './shared';

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  .title {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
  }
`;

const RangeChip = styled('span')`
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  background: #eef1f4;
  border-radius: 100px;
  padding: 4px 10px;
`;

const TotalsCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
`;

const HeroLabel = styled('div')`
  font-size: 10px;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const HeroValue = styled('div')`
  font-size: 24px;
  font-weight: 800;
  color: ${COLORS.PRIMARY};
  margin-bottom: 12px;
`;

const TotalsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  border-top: 1px solid #f1f5f9;
  padding-top: 10px;

  .label {
    font-size: 9px;
    color: #94a3b8;
    margin-bottom: 3px;
    white-space: nowrap;
  }

  .value {
    font-size: 12.5px;
    font-weight: 700;
    color: #0e1c2b;
    white-space: nowrap;
  }
`;

const DraftStrip = styled('div')<{
  visible: boolean;
  reduced: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #eef3ff;
  border: 1px solid #d4e0ff;
  border-radius: 10px;
  padding: 9px 12px;
  margin-bottom: 12px;
  font-size: 11px;
  color: #0e1c2b;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease 250ms, transform 0.5s ease 250ms;

  strong {
    font-weight: 700;
  }

  .review {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    white-space: nowrap;
  }
`;

const ToolbarRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const SearchBox = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  color: #94a3b8;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  width: 100%;
  box-sizing: border-box;
`;

const TableCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  overflow: hidden;
`;

const Table = styled('table')`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;

  th {
    text-align: left;
    font-size: 9.5px;
    font-weight: 500;
    color: #b0b7c3;
    padding: 10px 12px 6px;
    white-space: nowrap;

    &.num {
      text-align: right;
    }
  }

  td {
    padding: 9px 12px;
    color: #334155;
    white-space: nowrap;
    vertical-align: middle;

    &.num {
      text-align: right;
    }
  }
`;

const AnimatedRow = styled('tr')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  border-top: 1px solid #f4f4f2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const SwapCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #0e1c2b;

  svg {
    color: #94a3b8;
    flex-shrink: 0;
  }
`;

const Lift = styled('span')`
  font-weight: 700;
  color: #389e0d;
`;

type Opportunity = {
  to: { name: string; initial: string; color: string };
  clicks: string;
  lift: string;
};

const opportunities: Opportunity[] = [
  {
    to: { name: DEMO_MERCHANTS.retailPartnerShort, initial: 'R', color: COLORS.PRIMARY_DARK },
    clicks: '23.4K',
    lift: '+$14.5K'
  },
  {
    to: { name: 'Samsung', initial: 'S', color: '#1428a0' },
    clicks: '17.1K',
    lift: '+$8.2K'
  },
  {
    to: { name: 'Adorama', initial: 'A', color: '#0e1c2b' },
    clicks: '9.4K',
    lift: '+$5.1K'
  },
  {
    to: { name: 'Home Depot', initial: 'H', color: '#f96302' },
    clicks: '6.2K',
    lift: '+$2.8K'
  }
];

export const YieldOptimizerMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });

  return (
    <ProductMockupFrame ref={ref}>
      <AppMockupChrome product="Amplify" surface="Yield optimization" />
      <ProductMockupBody>
        <TitleRow>
          <span className="title">Yield optimization</span>
          <RangeChip>Based on Last 30 Days</RangeChip>
        </TitleRow>

        <TotalsCard>
          <HeroLabel>Monthly incremental revenue</HeroLabel>
          <HeroValue>
            +$
            <AnimatedNumber
              value={77.9}
              start={inView}
              format={(n) => n.toFixed(1)}
            />
            K
          </HeroValue>
          <TotalsGrid>
            <div>
              <div className="label">Annualized</div>
              <div className="value">$935K</div>
            </div>
            <div>
              <div className="label">Swappable clicks</div>
              <div className="value">56.1K</div>
            </div>
            <div>
              <div className="label">Opportunities</div>
              <div className="value">23</div>
            </div>
            <div>
              <div className="label">Merchants</div>
              <div className="value">9 → 14</div>
            </div>
          </TotalsGrid>
        </TotalsCard>

        <DraftStrip visible={inView} reduced={reduced}>
          <Sparkles size={13} color={COLORS.PRIMARY} />
          <span>
            <strong>AI draft ready:</strong> {DEMO_MERCHANTS.retailPartner} swap · 11 pages
          </span>
          <span className="review">
            Review <ArrowRight size={11} />
          </span>
        </DraftStrip>

        <ToolbarRow>
          <SearchBox>
            <Search size={11} /> Search swap opportunities
          </SearchBox>
        </ToolbarRow>

        <TableCard>
          <Table>
            <thead>
              <tr>
                <th>Swap to</th>
                <th className="num">Clicks</th>
                <th className="num">Est. lift / mo</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map((opp, index) => (
                <AnimatedRow
                  key={opp.to.name}
                  visible={inView}
                  delayMs={350 + index * 110}
                  reduced={reduced}
                >
                  <td>
                    <SwapCell>
                      <ArrowRight size={11} />
                      <MerchantDot bg={opp.to.color}>
                        {opp.to.initial}
                      </MerchantDot>
                      {opp.to.name}
                    </SwapCell>
                  </td>
                  <td className="num">{opp.clicks}</td>
                  <td className="num">
                    <Lift>{opp.lift}</Lift>
                  </td>
                </AnimatedRow>
              ))}
            </tbody>
          </Table>
        </TableCard>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
